import React from 'react';
import { useColorTheme } from '../context/ColorThemeContext';

const CreditCostPreview = ({ costPreview, onProceed, onCancel, onPurchaseCredits }) => {
  const { currentColors, colorTheme } = useColorTheme();

  if (!costPreview) return null;

  const { cost_breakdown, current_balance, can_generate, shortfall, is_power_user, video_time_estimate, language_info, language_count } = costPreview;
  const { gross_cost, free_tier_discount, net_cost, using_free_tier } = cost_breakdown;

  const getThemeClasses = () => {
    if (colorTheme === 'purple') {
      return {
        gradient: 'from-purple-500 to-pink-500',
        button: 'bg-purple-600 hover:bg-purple-700',
        border: 'border-purple-200 dark:border-purple-700',
        badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
        lightBg: 'bg-purple-50 dark:bg-purple-900'
      };
    } else if (colorTheme === 'blue') {
      return {
        gradient: 'from-blue-500 to-sky-500',
        button: 'bg-blue-600 hover:bg-blue-700',
        border: 'border-blue-200 dark:border-blue-700',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        lightBg: 'bg-blue-50 dark:bg-blue-900'
      };
    }
    return {
      gradient: 'from-green-500 to-emerald-500',
      button: 'bg-green-600 hover:bg-green-700',
      border: 'border-green-200 dark:border-green-700',
      badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      lightBg: 'bg-green-50 dark:bg-green-900'
    };
  };

  const theme = getThemeClasses();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onCancel}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-4`}>
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white flex items-center">
                <span className="mr-2">💰</span>
                {can_generate ? 'Confirm Generation' : 'Insufficient Credits'}
              </h3>
              <button
                onClick={onCancel}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {/* Power User Badge */}
            {is_power_user && (
              <div className={`mb-4 p-4 ${theme.lightBg} rounded-lg border-2 ${theme.border}`}>
                <div className="flex items-center">
                  <span className="text-2xl mr-3">⭐</span>
                  <div>
                    <p className="font-bold text-gray-900 dark:text-white">Power User</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      You have unlimited generation with no credit charges
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Current Balance */}
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 dark:text-gray-300">Your Balance</span>
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  {current_balance} credits
                </span>
              </div>
            </div>

            {/* Cost Breakdown */}
            {!is_power_user && (
              <div className="mb-4 space-y-3">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Cost Breakdown</h4>
                
                {/* Gross Cost */}
                {gross_cost.text_ads > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      📝 {gross_cost.text_ads} Text Ad{gross_cost.text_ads > 1 ? 's' : ''} × {gross_cost.text_credit_cost || 3}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {gross_cost.text_credits} credits
                    </span>
                  </div>
                )}
                
                {gross_cost.image_ads > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      🖼️ {gross_cost.image_ads} Image Ad{gross_cost.image_ads > 1 ? 's' : ''} × {gross_cost.image_credit_cost || 15}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {gross_cost.image_credits} credits
                    </span>
                  </div>
                )}
                
                {gross_cost.video_ads > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      🎬 {gross_cost.video_ads} Video Ad{gross_cost.video_ads > 1 ? 's' : ''} × {gross_cost.video_credit_cost || 50}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {gross_cost.video_credits} credits
                    </span>
                  </div>
                )}

                {/* Multi-Language Video Info — only show when videos are actually being generated */}
                {language_info && language_info.count > 1 && gross_cost.video_ads > 0 && (
                  <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">🌐</span>
                      <div className="flex-1">
                        <p className="font-medium text-amber-800 dark:text-amber-200 text-sm">
                          {language_info.count} Language Videos
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          {language_info.names ? language_info.names.join(', ') : language_info.note}
                        </p>
                        {cost_breakdown.additional_video_cost && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium">
                            Additional cost: +{cost_breakdown.additional_video_cost} credits ({language_info.count - 1} extra videos)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Multi-Language Image Info (Ultra Image + localized images toggle) */}
                {cost_breakdown.image_language_count > 1 && cost_breakdown.additional_image_cost > 0 && (
                  <div className="mt-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-700">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">🖼️</span>
                      <div className="flex-1">
                        <p className="font-medium text-emerald-800 dark:text-emerald-200 text-sm">
                          {cost_breakdown.image_language_count} Language Images
                        </p>
                        <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                          {language_info && language_info.names
                            ? language_info.names.join(', ')
                            : `Each image generated in ${cost_breakdown.image_language_count} languages with native on-image text`}
                        </p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 font-medium">
                          Additional cost: +{cost_breakdown.additional_image_cost} credits ({cost_breakdown.image_language_count - 1} extra language versions)
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Multi-Language Text Ad Info */}
                {cost_breakdown.text_language_count > 1 && cost_breakdown.additional_text_cost > 0 && (
                  <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">📝</span>
                      <div className="flex-1">
                        <p className="font-medium text-blue-800 dark:text-blue-200 text-sm">
                          {cost_breakdown.text_language_count} Language Text Ads
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          Each text ad will be generated in {cost_breakdown.text_language_count} languages (including translations)
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">
                          Additional cost: +{cost_breakdown.additional_text_cost} credits ({cost_breakdown.text_language_count - 1} extra language versions)
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Video Generation Time Estimate */}
                {video_time_estimate && (
                  <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-700">
                    <div className="flex items-center text-sm">
                      <span className="mr-2">⏱️</span>
                      <span className="text-amber-700 dark:text-amber-300 font-medium">
                        Estimated Time: {video_time_estimate}
                      </span>
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Subtotal</span>
                    <span className="font-bold text-gray-900 dark:text-white">
                      {gross_cost.total_credits} credits
                    </span>
                  </div>
                </div>

                {/* Free Tier Discount */}
                {using_free_tier && (
                  <div className={`p-3 ${theme.lightBg} rounded-lg`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center">
                        <span className="mr-2">🎁</span>
                        Free Tier Discount
                      </span>
                      <span className="font-bold text-green-600 dark:text-green-400">
                        -{free_tier_discount.total_credits} credits
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      You get 1 free text, image, and video ad per month!
                    </p>
                  </div>
                )}

                {/* Final Cost */}
                <div className="border-t-2 border-gray-300 dark:border-gray-600 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">Total Cost</span>
                    <span className={`text-2xl font-extrabold ${can_generate ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {net_cost} credits
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Insufficient Credits Warning */}
            {!can_generate && !is_power_user && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg">
                <div className="flex items-start">
                  <span className="text-2xl mr-3">⚠️</span>
                  <div>
                    <p className="font-bold text-red-800 dark:text-red-200 mb-1">
                      Insufficient Credits
                    </p>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      You need {shortfall} more credits to generate this campaign.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
            
            {can_generate ? (
              <button
                onClick={onProceed}
                className={`px-6 py-2 ${theme.button} text-white rounded-lg font-medium transition-colors`}
              >
                {is_power_user ? 'Generate Campaign' : `Generate for ${net_cost} Credits`}
              </button>
            ) : (
              <button
                onClick={onPurchaseCredits}
                className={`px-6 py-2 ${theme.button} text-white rounded-lg font-medium transition-colors`}
              >
                Purchase Credits
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditCostPreview;
